import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import FirstLogin from "@/pages/FirstLogin";
import Activate from "@/pages/Activate";
import Setup from "@/pages/Setup";
import Dashboard from "@/pages/Dashboard";
import Checklist from "@/pages/Checklist";
import Budget from "@/pages/Budget";
import Savings from "@/pages/Savings";
import Guests from "@/pages/Guests";
import Vendors from "@/pages/Vendors";
import Timeline from "@/pages/Timeline";
import Documents from "@/pages/Documents";
import Moodboard from "@/pages/Moodboard";
import Honeymoon from "@/pages/Honeymoon";
import CoupleTalk from "@/pages/CoupleTalk";
import LandingPage from "@/pages/LandingPage";
import ScanWa from "@/pages/ScanWa";
import AI from "@/pages/AI";
import AiPrewedding from "@/pages/AiPrewedding";
import ComingSoon from "@/pages/ComingSoon";
import Settings from "@/pages/Settings";
import AdminPreweddingTemplates from "@/pages/admin/AdminPreweddingTemplates";
import PremiumPaywall from "@/components/app/PremiumPaywall";
import { AI_PREWEDDING_ENABLED } from "@/lib/features";

// Code-split untuk halaman admin/editor Undangan Digital.
const InvitationPage = lazy(() => import("@/pages/InvitationPage"));
const InvitationDashboard = lazy(() => import("@/pages/InvitationDashboard"));
const InvitationEditor = lazy(() => import("@/pages/InvitationEditor"));
const HandoverMahar = lazy(() => import("@/pages/HandoverMahar"));

function Splash() {
  return (
    <div className="grid min-h-[100svh] place-items-center">
      <div
        className="text-sm opacity-60"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Menyiapkan&rsquo;
      </div>
    </div>
  );
}
import Admin from "@/pages/Admin";
import AccountExpired from "@/pages/AccountExpired";
import AppShell from "@/components/app/AppShell";
import { ErrorBoundary } from "@/components/app/ErrorBoundary";
import { Loader2 } from "lucide-react";

function Protected({ children, requireWedding = true, requireSetup = true }) {
  const { loading, user, wedding, membership, accountBlocked, isAdmin } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  // Akun kedaluwarsa punya halaman sendiri, jangan lempar ke /login.
  if (accountBlocked) return <Navigate to="/account-expired" replace />;
  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin) return children;
  if (requireWedding && !membership) return <Navigate to="/activate" replace />;
  if (requireSetup && wedding && !wedding.setup_complete) return <Navigate to="/setup" replace />;
  return children;
}

function AdminGuard({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PremiumGuard({ children, featureName, description }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return null;
  if (isAdmin || user?.plan_type === "premium") return children;
  return <PremiumPaywall featureName={featureName} description={description} />;
}

// AI Prewedding masih dalam tahap pengembangan. Admin tetap bisa membukanya
// untuk uji coba; pengguna biasa melihat halaman "dalam pengembangan" bila
// mengakses URL-nya langsung.
function PreweddingGate({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (AI_PREWEDDING_ENABLED || isAdmin) return children;
  return <ComingSoon />;
}

function Router() {
  return (
    <Routes>
      {/* Publik: di luar <Protected> dan <AppShell>. */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/invitation/:slug"
        element={
          <Suspense fallback={<Splash />}>
            <InvitationPage />
          </Suspense>
        }
      />
      <Route path="/login" element={<Login />} />
      <Route path="/scan-wa" element={<ScanWa />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/account-expired" element={<AccountExpired />} />
      <Route path="/register" element={<Register />} />
      <Route path="/first-login" element={<Protected requireWedding={false} requireSetup={false}><FirstLogin /></Protected>} />
      <Route path="/activate" element={<Protected requireWedding={false} requireSetup={false}><Activate /></Protected>} />
      <Route path="/setup" element={<Protected requireSetup={false}><Setup /></Protected>} />
      <Route path="/dashboard" element={<Protected><AppShell><Dashboard /></AppShell></Protected>} />
      <Route
        path="/handover"
        element={<Navigate to="/handover/handover" replace />}
      />
      <Route
        path="/handover/:jenis"
        element={
          <Protected>
            <AppShell>
              <Suspense fallback={<Splash />}>
                <HandoverMahar />
              </Suspense>
            </AppShell>
          </Protected>
        }
      />
      <Route
        path="/invitations"
        element={
          <Protected requireSetup={false}>
            <AppShell>
              <PremiumGuard featureName="Undangan Digital" description="Fitur Undangan Digital online publik hanya tersedia untuk akun Paket Premium.">
                <Suspense fallback={<Splash />}>
                  <InvitationDashboard />
                </Suspense>
              </PremiumGuard>
            </AppShell>
          </Protected>
        }
      />
      <Route
        path="/invitations/:id/edit"
        element={
          <Protected requireSetup={false}>
            <AppShell>
              <PremiumGuard featureName="Undangan Digital" description="Fitur Undangan Digital online publik hanya tersedia untuk akun Paket Premium.">
                <Suspense fallback={<Splash />}>
                  <InvitationEditor />
                </Suspense>
              </PremiumGuard>
            </AppShell>
          </Protected>
        }
      />
      <Route path="/checklist" element={<Protected><AppShell><Checklist /></AppShell></Protected>} />
      <Route path="/budget" element={<Protected><AppShell><Budget /></AppShell></Protected>} />
      <Route path="/savings" element={<Protected><AppShell><Savings /></AppShell></Protected>} />
      <Route path="/guests" element={<Protected><AppShell><Guests /></AppShell></Protected>} />
      <Route path="/vendors" element={<Protected><AppShell><Vendors /></AppShell></Protected>} />
      <Route path="/timeline" element={<Protected><AppShell><Timeline /></AppShell></Protected>} />
      <Route path="/documents" element={<Protected><AppShell><Documents /></AppShell></Protected>} />
      <Route path="/couple-talk" element={<Protected><AppShell><PremiumGuard featureName="Couple Talk" description="Membangun komunikasi mendalam dan mempererat hubungan dengan pasangan lewat permainan kartu interaktif."><CoupleTalk /></PremiumGuard></AppShell></Protected>} />
      <Route path="/honeymoon" element={<Protected><AppShell><PremiumGuard featureName="Honeymoon Planner" description="Fitur Honeymoon Planner terintegrasi hanya tersedia untuk pengguna Paket Premium."><Honeymoon /></PremiumGuard></AppShell></Protected>} />
      <Route path="/honeymoon/:tab" element={<Protected><AppShell><PremiumGuard featureName="Honeymoon Planner" description="Fitur Honeymoon Planner terintegrasi hanya tersedia untuk pengguna Paket Premium."><Honeymoon /></PremiumGuard></AppShell></Protected>} />
      <Route path="/moodboard" element={<Protected><AppShell><PremiumGuard featureName="Moodboard" description="Fitur Moodboard visual perencanaan pernikahan hanya tersedia untuk akun Paket Premium."><Moodboard /></PremiumGuard></AppShell></Protected>} />
      <Route path="/ai" element={<Protected><AppShell><PremiumGuard featureName="Weddly AI" description="Asisten Weddly AI tanpa batas hanya tersedia untuk akun Paket Premium."><AI /></PremiumGuard></AppShell></Protected>} />
      <Route path="/ai-prewedding" element={<Protected><AppShell><PreweddingGate><PremiumGuard featureName="AI Prewedding Studio" description="Fitur pembuatan foto AI Prewedding hanya tersedia untuk akun Paket Premium."><AiPrewedding /></PremiumGuard></PreweddingGate></AppShell></Protected>} />
      <Route path="/settings" element={<Protected><AppShell><Settings /></AppShell></Protected>} />
      <Route path="/admin/pending" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/analytics" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/activity" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/whatsapp" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/accounts" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/quotes" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/music" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/feedback" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/logs" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><Admin /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin/prewedding-templates" element={<Protected requireWedding={false} requireSetup={false}><AdminGuard><AppShell><AdminPreweddingTemplates /></AppShell></AdminGuard></Protected>} />
      <Route path="/admin" element={<Navigate to="/admin/pending" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <Router />
              <Toaster richColors position="top-right" />
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}